import { useCamera } from '@hooks/useCamera';
import { type FC, type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CameraIcon, CameraSwitchIcon, CheckIcon, CloseIcon, RetryIcon, ScanCardIcon, SpinnerIcon, UploadIcon } from './icons';
import {
	CameraActionRow,
	CameraCaptureButton,
	CameraErrorState,
	CameraGhostButton,
	CameraModalCard,
	CameraModalFooter,
	CameraModalHeader,
	CameraModalOverlay,
	CameraOverlayGuide,
	CameraPreviewFrame,
	CameraStatusRow,
	CameraSwitchPill,
} from './styled';

/**
 * Desktop in-browser camera capture for the IdentityVerificationCard.
 *
 * Why this component exists:
 *  - On mobile, `<input type="file" capture="user|environment">` opens the
 *    native camera with the correct facing direction. That path stays — see
 *    `IdentityVerificationCard` where the modal is only opened when
 *    `isTouchDevice` is false.
 *  - On desktop, `capture` is silently ignored and the file picker opens —
 *    a confusing UX given the button reads "Take selfie" / "Scan card". This
 *    modal fills that gap with a real live-preview camera flow via getUserMedia.
 *
 * Contract:
 *  - `onCapture(file)` receives a `File` (image/jpeg) ready to be fed into the
 *    existing `handleSelfieFile` / `handleCardFile` pipeline. The file flows
 *    through the same normalise → media-manager → KYC path as a mobile capture.
 *  - `onUploadFallback()` is called when the user has no camera, permission was
 *    denied, or the device is busy. The caller is expected to trigger its own
 *    `<input type="file">` gallery picker.
 */

interface CameraCaptureModalProps {
	open: boolean;
	mode: 'selfie' | 'card';
	onClose: () => void;
	onCapture: (file: File) => void;
	onUploadFallback: () => void;
}

const COPY = {
	selfie: {
		eyebrow: 'Step 1 of 4',
		title: 'Take a clear selfie',
		hint: 'Look straight at the camera and keep your face inside the oval.',
		captureLabel: 'Capture photo',
		fileName: 'selfie.jpg',
	},
	card: {
		eyebrow: 'Step 1 of 4',
		title: 'Scan your voter’s card',
		hint: 'Place your card on a flat surface and align its edges with the box.',
		captureLabel: 'Capture card',
		fileName: 'voter-card.jpg',
	},
} as const;

const CameraCaptureModal: FC<CameraCaptureModalProps> = ({ open, mode, onClose, onCapture, onUploadFallback }) => {
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const closeButtonRef = useRef<HTMLButtonElement | null>(null);
	const captureButtonRef = useRef<HTMLButtonElement | null>(null);

	const camera = useCamera({ facingMode: mode === 'selfie' ? 'user' : 'environment' });

	const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
	const [capturedUrl, setCapturedUrl] = useState<string | null>(null);
	const [capturing, setCapturing] = useState<boolean>(false);

	const mirror = mode === 'selfie'; // mirror only the preview; capturePhoto draws the un-mirrored frame
	const copy = COPY[mode];

	// Start the stream when the modal opens; release it on close or unmount.
	useEffect(() => {
		if (!open) return;
		void camera.start();
		return () => {
			camera.stop();
		};
	}, [open, mode]);

	// Wire the stream into the <video> element whenever it changes.
	useEffect(() => {
		const v = videoRef.current;
		if (!v) return;
		if (camera.stream) {
			v.srcObject = camera.stream;
			// Some browsers (older Safari) need an explicit play() after srcObject set.
			void v.play().catch(() => {
				/* ignored — happens on tab-switch races, autoplay is best-effort */
			});
		} else {
			v.srcObject = null;
		}
	}, [camera.stream]);

	// Body scroll lock + focus management while open.
	useEffect(() => {
		if (!open) return;
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		// Move focus into the modal so Escape works and keyboard users land here.
		const focusTimer = window.setTimeout(() => {
			(captureButtonRef.current ?? closeButtonRef.current)?.focus();
		}, 50);

		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				onClose();
			}
		};
		window.addEventListener('keydown', onKey);

		return () => {
			document.body.style.overflow = prevOverflow;
			window.clearTimeout(focusTimer);
			window.removeEventListener('keydown', onKey);
		};
	}, [open, onClose]);

	// Revoke the captured-frame object URL when it changes or when we close.
	useEffect(() => {
		if (!capturedUrl) return;
		return () => URL.revokeObjectURL(capturedUrl);
	}, [capturedUrl]);

	// Clear local capture state whenever the modal toggles closed so the next
	// open shows a fresh live preview rather than a stale "Use this photo" screen.
	useEffect(() => {
		if (!open) {
			setCapturedBlob(null);
			setCapturedUrl(null);
			setCapturing(false);
		}
	}, [open]);

	const handleCapture = useCallback(async () => {
		const v = videoRef.current;
		if (!v || v.readyState < 2) return; // need HAVE_CURRENT_DATA or better
		setCapturing(true);
		try {
			const blob = await camera.capturePhoto(v, mirror);
			if (!blob) {
				setCapturing(false);
				return;
			}
			const url = URL.createObjectURL(blob);
			setCapturedBlob(blob);
			setCapturedUrl(url);
		} finally {
			setCapturing(false);
		}
	}, [camera, mirror]);

	const handleRetake = useCallback(() => {
		setCapturedBlob(null);
		setCapturedUrl((prev) => {
			if (prev) URL.revokeObjectURL(prev);
			return null;
		});
	}, []);

	const handleConfirm = useCallback(() => {
		if (!capturedBlob) return;
		const file = new File([capturedBlob], copy.fileName, { type: 'image/jpeg', lastModified: Date.now() });
		onCapture(file);
		camera.stop();
		onClose();
	}, [capturedBlob, copy.fileName, onCapture, onClose, camera]);

	const handleClose = useCallback(() => {
		camera.stop();
		onClose();
	}, [camera, onClose]);

	const handleSwitchCamera = useCallback(async () => {
		if (camera.devices.length < 2) return;
		const idx = camera.devices.findIndex((d) => d.deviceId === camera.activeDeviceId);
		const next = camera.devices[(idx + 1) % camera.devices.length];
		if (next) await camera.switchDevice(next.deviceId);
	}, [camera]);

	const handleUploadFallback = useCallback(() => {
		camera.stop();
		onClose();
		// Defer so the modal close finishes before the file picker opens; some
		// browsers refuse to open a picker that's racing with another DOM change.
		window.setTimeout(() => onUploadFallback(), 0);
	}, [camera, onClose, onUploadFallback]);

	const errorState = useMemo(() => {
		switch (camera.status) {
			case 'denied':
				return {
					icon: '🔒',
					title: 'Camera access needed',
					body:
						camera.error ?? 'We need permission to use your camera. Allow access in the browser address bar, then tap Try again.',
				};
			case 'no_device':
				return {
					icon: '🎥',
					title: 'No camera found',
					body: camera.error ?? "We couldn't find a camera on this device. Upload a photo instead.",
				};
			case 'busy':
				return {
					icon: '⚙️',
					title: 'Camera in use',
					body: camera.error ?? 'Your camera is being used by another app. Close it and try again.',
				};
			case 'unsupported':
				return {
					icon: '⚠️',
					title: 'Camera not supported',
					body: camera.error ?? "This browser doesn't support in-page camera capture. Upload a photo instead.",
				};
			case 'error':
				return {
					icon: '⚠️',
					title: 'Something went wrong',
					body: camera.error ?? 'We couldn’t start the camera. Try again or upload a photo.',
				};
			default:
				return null;
		}
	}, [camera.status, camera.error]);

	if (!open) return null;

	const showCapturedReview = capturedUrl != null;
	const showLivePreview = !showCapturedReview && !errorState;
	const showMultiCamSwitch = showLivePreview && camera.devices.length > 1;

	return (
		<CameraModalOverlay
			role='dialog'
			aria-modal='true'
			aria-labelledby='camera-modal-title'
			onClick={(e: MouseEvent<HTMLDivElement>) => {
				// Click-on-backdrop closes (but not click-on-card).
				if (e.target === e.currentTarget) handleClose();
			}}
		>
			<CameraModalCard>
				<CameraModalHeader>
					<div className='titleblock'>
						<p className='eyebrow'>{copy.eyebrow}</p>
						<h3
							className='title'
							id='camera-modal-title'
						>
							{showCapturedReview ? 'Looks good?' : copy.title}
						</h3>
					</div>
					<button
						ref={closeButtonRef}
						className='closeBtn'
						type='button'
						aria-label='Close camera'
						onClick={handleClose}
					>
						<CloseIcon size={18} />
					</button>
				</CameraModalHeader>

				<CameraPreviewFrame mode={mode}>
					{showLivePreview && (
						<>
							<video
								ref={videoRef}
								autoPlay
								playsInline
								muted
								style={{ transform: mirror ? 'scaleX(-1)' : 'none' }}
							/>
							<CameraOverlayGuide mode={mode}>
								{mode === 'selfie' ? (
									<div className='oval' />
								) : (
									<div className='cardFrame'>
										<span className='corner tl' />
										<span className='corner tr' />
										<span className='corner bl' />
										<span className='corner br' />
									</div>
								)}
							</CameraOverlayGuide>
							{showMultiCamSwitch && (
								<CameraSwitchPill
									type='button'
									onClick={handleSwitchCamera}
									aria-label='Switch camera'
								>
									<CameraSwitchIcon size={14} />
									Switch camera
								</CameraSwitchPill>
							)}
						</>
					)}

					{showCapturedReview && capturedUrl && (
						<img
							src={capturedUrl}
							alt={mode === 'selfie' ? 'Captured selfie preview' : 'Captured card preview'}
						/>
					)}

					{errorState && (
						<CameraErrorState>
							<span
								className='errIcon'
								aria-hidden
							>
								{errorState.icon}
							</span>
							<h4 className='errTitle'>{errorState.title}</h4>
							<p className='errBody'>{errorState.body}</p>
						</CameraErrorState>
					)}

					{camera.status === 'requesting' && !errorState && !showCapturedReview && (
						<div
							className='placeholderState'
							aria-live='polite'
						>
							<SpinnerIcon size={28} />
							<span>Starting camera…</span>
						</div>
					)}
				</CameraPreviewFrame>

				{!errorState && (
					<CameraStatusRow aria-live='polite'>
						{showCapturedReview
							? mode === 'selfie'
								? 'If your face is clear and well-lit, tap Use this photo.'
								: 'If the VIN is readable, tap Use this photo.'
							: copy.hint}
					</CameraStatusRow>
				)}

				<CameraModalFooter>
					{showLivePreview && (
						<>
							<CameraCaptureButton
								ref={captureButtonRef}
								type='button'
								onClick={handleCapture}
								disabled={camera.status !== 'streaming' || capturing}
							>
								{mode === 'selfie' ? <CameraIcon size={20} /> : <ScanCardIcon size={20} />}
								{capturing ? 'Capturing…' : copy.captureLabel}
							</CameraCaptureButton>
							<CameraActionRow>
								<CameraGhostButton
									type='button'
									onClick={handleUploadFallback}
								>
									<UploadIcon size={16} />
									Upload instead
								</CameraGhostButton>
							</CameraActionRow>
						</>
					)}

					{showCapturedReview && (
						<CameraActionRow>
							<CameraGhostButton
								type='button'
								onClick={handleRetake}
							>
								<RetryIcon size={16} />
								Retake
							</CameraGhostButton>
							<CameraGhostButton
								variant='primary'
								type='button'
								onClick={handleConfirm}
							>
								<CheckIcon size={16} />
								Use this photo
							</CameraGhostButton>
						</CameraActionRow>
					)}

					{errorState && (
						<CameraActionRow>
							<CameraGhostButton
								variant='primary'
								type='button'
								onClick={handleUploadFallback}
							>
								<UploadIcon size={16} />
								Upload a photo instead
							</CameraGhostButton>
							{camera.status !== 'unsupported' && (
								<CameraGhostButton
									type='button'
									onClick={() => void camera.start()}
								>
									<RetryIcon size={16} />
									Try again
								</CameraGhostButton>
							)}
						</CameraActionRow>
					)}
				</CameraModalFooter>
			</CameraModalCard>
		</CameraModalOverlay>
	);
};

export default CameraCaptureModal;
